import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import PlanificationTypeService from '../../services/planificationType.service';

const PlanificationTypeForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allTypes, setAllTypes] = useState([]);
  const [form, setForm] = useState({ name: '', parent_id: '' });
  const [sections, setSections] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadAllTypes();
  }, []);

  useEffect(() => {
    if (isEdit) {
      fetchType();
    } else {
      const parentId = searchParams.get('parent_id');
      if (parentId) {
        setForm(prev => ({ ...prev, parent_id: parentId }));
      }
    }
  }, [id, isEdit]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadAllTypes = async () => {
    try {
      const res = await PlanificationTypeService.getAll();
      setAllTypes(res.data?.data || []);
    } catch (e) {
      console.error('Error loading types:', e);
    }
  };

  const fetchType = async () => {
    setLoading(true);
    try {
      const res = await PlanificationTypeService.get(id);
      const type = res.data?.data;
      setForm({
        name: type.name || '',
        parent_id: type.parent_id || '',
      });
      const incoming = (type.sections || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setSections(incoming.map(s => ({
        id: s.id,
        name: s.name || '',
        default_text: s.default_text || '',
        sort_order: s.sort_order || 0,
      })));
    } catch (e) {
      console.error('Error loading planification type:', e);
      showSnackbar(t('planifications.errorLoadingPlanificationType'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const parentOptions = useMemo(() => {
    return allTypes.filter(t => String(t.id) !== String(id));
  }, [allTypes, id]);

  const handleSectionChange = (idx, field, value) => {
    setSections(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addSection = () => {
    setSections(prev => ([
      ...prev,
      { name: '', default_text: '', sort_order: prev.length },
    ]));
  };

  const removeSection = (idx) => {
    setSections(prev => prev.filter((_, i) => i !== idx));
  };

  const moveSection = (idx, dir) => {
    setSections(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[idx];
      next[idx] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showSnackbar(t('common.snackbar.nameRequired'), 'error');
      return;
    }

    const payload = {
      name: form.name.trim(),
      parent_id: form.parent_id || null,
      sections: sections
        .map((s, idx) => ({
          ...(s.id ? { id: s.id } : {}),
          name: (s.name || '').trim(),
          default_text: s.default_text || null,
          sort_order: idx,
        }))
        .filter(s => s.name),
    };

    setSaving(true);
    try {
      if (isEdit) {
        await PlanificationTypeService.update(id, payload);
        showSnackbar(t('planifications.planificationTypeUpdated'));
      } else {
        await PlanificationTypeService.create(payload);
        showSnackbar(t('planifications.planificationTypeCreated'));
      }
      navigate('/planifications/types');
    } catch (e) {
      console.error('Error saving planification type:', e);
      const msg = e.response?.data?.message || (e.response?.data?.errors ? Object.values(e.response.data.errors).flat().join(', ') : 'Error saving');
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/planifications/types')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">{isEdit ? t('planifications.editPlanificationType') : t('planifications.newPlanificationType')}</Typography>
          </Box>
        }
        action={
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={saving}>
            {t('common.save')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('common.name')}
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('planifications.parentOptional')}</InputLabel>
              <Select
                label={t('planifications.parentOptional')}
                value={form.parent_id}
                onChange={(e) => setForm(prev => ({ ...prev, parent_id: e.target.value }))}
              >
                <MenuItem value="">{t('planifications.none')}</MenuItem>
                {parentOptions.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">{t('planifications.sections')}</Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addSection}>
              {t('planifications.addSection')}
            </Button>
          </Box>

          {sections.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography color="text.secondary">{t('planifications.noSectionsYet')}</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sections.map((s, idx) => (
                <Paper key={s.id || idx} variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('planifications.sectionNumber', { number: idx + 1 })}</Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton size="small" onClick={() => moveSection(idx, -1)} disabled={idx === 0} title={t('planifications.moveUp')}>
                        <UpIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1} title={t('planifications.moveDown')}>
                        <DownIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => removeSection(idx)} title={t('planifications.remove')}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={t('planifications.sectionName')}
                        value={s.name}
                        onChange={(e) => handleSectionChange(idx, 'name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label={t('planifications.defaultTextOptional')}
                        value={s.default_text}
                        onChange={(e) => handleSectionChange(idx, 'default_text', e.target.value)}
                        multiline
                        minRows={2}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </CardContent>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default PlanificationTypeForm;
